import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  BadgeCheck,
  CheckCircle2,
  CreditCard,
  Loader2,
  LockKeyhole,
  Percent,
  ReceiptText,
  Sparkles,
  Tag,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { BRAND_LOGO, formatINR } from "@/lib/constants";
import { toast } from "sonner";

export default function VendorSubscriptionCheckout() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, setAuthOpen } = useAuth();

  const planSlug = searchParams.get("plan") || "";

  const [checkout, setCheckout] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState("");
  const [loading, setLoading] = useState(true);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [paying, setPaying] = useState(false);
  const [success, setSuccess] = useState(false);

  const loadCheckout = async (code = "") => {
    if (!planSlug) {
      toast.error("No subscription plan selected");
      navigate("/vendor/dashboard");
      return;
    }

    try {
      setLoading(true);

      const { data } = await api.post("/vendor/subscription/checkout", {
        plan_slug: planSlug,
        coupon_code: code,
      });

      setCheckout(data);
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Could not load checkout details"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      setAuthOpen(true);
      return;
    }

    loadCheckout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, planSlug]);

  const applyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();

    if (!code) {
      toast.error("Enter a coupon code");
      return;
    }

    try {
      setApplyingCoupon(true);

      await loadCheckout(code);

      setAppliedCoupon(code);
      toast.success("Coupon applied successfully");
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Could not apply coupon"
      );
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = async () => {
    setCouponCode("");
    setAppliedCoupon("");

    try {
      await loadCheckout("");
      toast.success("Coupon removed");
    } catch (e) {
      toast.error("Could not remove coupon");
    }
  };

  const loadRazorpay = () =>
    new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const existing = document.querySelector(
        'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
      );

      if (existing) {
        existing.addEventListener(
          "load",
          () => resolve(true),
          { once: true }
        );
        existing.addEventListener(
          "error",
          () => resolve(false),
          { once: true }
        );
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const payNow = async () => {
    if (!checkout?.pricing?.total_amount) {
      toast.error("Checkout amount is unavailable");
      return;
    }

    try {
      setPaying(true);

      const loaded = await loadRazorpay();

      if (!loaded) {
        toast.error("Could not load Razorpay Checkout");
        setPaying(false);
        return;
      }

      /*
       * The backend recalculates and validates the coupon again here.
       * The frontend amount is never trusted for payment.
       */
      const paymentRes = await api.post(
        "/vendor/subscription/create-payment",
        {
          plan_slug: planSlug,
          coupon_code: appliedCoupon || "",
        }
      );

      const data = paymentRes.data;
      const rzp = data?.razorpay;

      if (!rzp?.key || !rzp?.order_id) {
        toast.error("Razorpay checkout details are unavailable");
        setPaying(false);
        return;
      }

      const options = {
        key: rzp.key,
        amount: rzp.amount,
        currency: rzp.currency || "INR",
        name: rzp.name || "MakeMyEventPro",
        description:
          rzp.description ||
          `${checkout.plan?.name || "Vendor"} Subscription`,
        order_id: rzp.order_id,
        prefill: rzp.prefill || {},
        theme: {
          color: "#7C3AED",
        },

        handler: async (response) => {
          try {
            const verifyRes = await api.post(
              "/vendor/subscription/verify-payment",
              {
                payment_id: data.payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }
            );

            if (verifyRes.data?.ok) {
              setSuccess(true);
              toast.success("Payment successful! Subscription activated.");
            } else {
              toast.error(
                "Payment verification failed. Subscription was not activated."
              );
            }
          } catch (e) {
            toast.error(
              e.response?.data?.detail ||
                "Payment verification failed. Subscription was not activated."
            );
          } finally {
            setPaying(false);
          }
        },

        modal: {
          ondismiss: () => {
            setPaying(false);
            toast.info(
              "Payment cancelled. Your subscription was not activated."
            );
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (e) {
      setPaying(false);
      toast.error(
        e.response?.data?.detail ||
          "Could not start subscription payment"
      );
    }
  };

  const downloadInvoice = () => {
    if (!checkout) return;

    const pricing = checkout.pricing || {};
    const billing = checkout.billing || {};
    const vendorName =
      user?.name ||
      "Vendor";

    const invoiceHtml = `
      <!doctype html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>MakeMyEventPro Invoice</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            padding: 40px;
            color: #222;
          }
          .header {
            display: flex;
            justify-content: space-between;
            border-bottom: 2px solid #7c3aed;
            padding-bottom: 20px;
            margin-bottom: 30px;
          }
          .brand {
            font-size: 24px;
            font-weight: 700;
            color: #7c3aed;
          }
          .muted {
            color: #666;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 25px;
          }
          td {
            padding: 10px 0;
            border-bottom: 1px solid #eee;
          }
          .total td {
            font-size: 18px;
            font-weight: 700;
            border-top: 2px solid #222;
          }
          .right {
            text-align: right;
          }
          .success {
            margin-top: 30px;
            padding: 15px;
            background: #f0fdf4;
            border: 1px solid #bbf7d0;
            color: #166534;
            border-radius: 8px;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="brand">MakeMyEventPro.com</div>
            <div class="muted">${billing.legal_name || billing.brand_name || "MakeMyEventPro"}</div>
            <div class="muted">${billing.registered_address || ""}</div>
            <div class="muted">GSTIN: ${billing.gstin || "N/A"}</div>
          </div>
          <div>
            <strong>SUBSCRIPTION INVOICE</strong>
            <div class="muted">${new Date().toLocaleDateString("en-IN")}</div>
          </div>
        </div>

        <h3>Bill To</h3>
        <p>
          <strong>${vendorName}</strong><br />
          ${user?.email || ""}<br />
          ${checkout.plan?.name || ""} Subscription
        </p>

        <table>
          <tr>
            <td>Plan Price</td>
            <td class="right">${formatINR(pricing.plan_price || 0)}</td>
          </tr>
          ${
            pricing.coupon_discount > 0
              ? `
          <tr>
            <td>Coupon Discount (${pricing.coupon_code || ""})</td>
            <td class="right">-${formatINR(pricing.coupon_discount)}</td>
          </tr>
          `
              : ""
          }
          <tr>
            <td>Taxable Amount</td>
            <td class="right">${formatINR(pricing.taxable_amount || 0)}</td>
          </tr>
          <tr>
            <td>GST (${pricing.gst_rate || 0}%)</td>
            <td class="right">${formatINR(pricing.gst_amount || 0)}</td>
          </tr>
          <tr class="total">
            <td>Total Paid</td>
            <td class="right">${formatINR(pricing.total_amount || 0)}</td>
          </tr>
        </table>

        <div class="success">
          Subscription activated successfully.
        </div>

        <p class="muted" style="margin-top:40px;">
          Thank you for choosing MakeMyEventPro.com
        </p>
      </body>
      </html>
    `;

    const win = window.open("", "_blank");

    if (!win) {
      toast.error("Please allow popups to download the invoice");
      return;
    }

    win.document.write(invoiceHtml);
    win.document.close();
    win.focus();
    win.print();
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600 mx-auto mb-3" />
          <p className="text-sm text-slate-500">
            Preparing your checkout...
          </p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <p className="text-slate-500">
          Please login to continue.
        </p>
      </div>
    );
  }

  const pricing = checkout?.pricing || {};
  const plan = checkout?.plan || {};
  const billing = checkout?.billing || {};
  const couponApplied =
    !!pricing.coupon_code && pricing.coupon_discount > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate("/vendor/dashboard")}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </Button>

          <div className="flex items-center gap-2 text-sm text-slate-500">
            <LockKeyhole className="h-4 w-4 text-green-600" />
            Secure Checkout
          </div>
        </div>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-[#5e17eb] to-[#cc00ff] flex items-center justify-center shadow-lg shadow-purple-200">
              <CreditCard className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
                Complete Your Subscription
              </h1>
              <p className="text-sm text-slate-500">
                Review your plan, apply your coupon and continue to secure payment.
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_380px] gap-6">
          <div className="space-y-6">
            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-5 w-5 text-purple-600" />
                    <span className="text-sm font-semibold text-purple-600">
                      SELECTED PLAN
                    </span>
                  </div>

                  <h2 className="text-2xl font-bold text-slate-900">
                    {plan.name}
                  </h2>

                  <p className="text-sm text-slate-500 mt-1">
                    Valid for {plan.validity_days || 30} days
                  </p>
                </div>

                <div className="text-right">
                  <div className="text-2xl font-bold bg-gradient-to-r from-[#5e17eb] to-[#cc00ff] bg-clip-text text-transparent">
                    {formatINR(pricing.plan_price || 0)}
                  </div>
                  <div className="text-xs text-slate-400">
                    plan price
                  </div>
                </div>
              </div>

              <div className="mt-6 grid sm:grid-cols-3 gap-3">
                <div className="rounded-2xl bg-purple-50 p-4">
                  <BadgeCheck className="h-5 w-5 text-purple-600 mb-2" />
                  <div className="font-semibold text-sm">
                    Premium Vendor
                  </div>
                  <div className="text-xs text-slate-500">
                    Professional tools
                  </div>
                </div>

                <div className="rounded-2xl bg-fuchsia-50 p-4">
                  <ReceiptText className="h-5 w-5 text-fuchsia-600 mb-2" />
                  <div className="font-semibold text-sm">
                    GST Invoice
                  </div>
                  <div className="text-xs text-slate-500">
                    Official billing
                  </div>
                </div>

                <div className="rounded-2xl bg-emerald-50 p-4">
                  <LockKeyhole className="h-5 w-5 text-emerald-600 mb-2" />
                  <div className="font-semibold text-sm">
                    Secure Payment
                  </div>
                  <div className="text-xs text-slate-500">
                    Razorpay Live
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Tag className="h-5 w-5 text-purple-600" />
                <h2 className="text-lg font-bold text-slate-900">
                  Coupon Code
                </h2>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  value={couponCode}
                  onChange={(e) =>
                    setCouponCode(e.target.value.toUpperCase())
                  }
                  placeholder="Enter coupon code"
                  disabled={applyingCoupon || paying}
                  className="h-12 rounded-xl uppercase"
                />

                {couponApplied ? (
                  <Button
                    variant="outline"
                    onClick={removeCoupon}
                    disabled={applyingCoupon || paying}
                    className="h-12 rounded-xl"
                  >
                    Remove
                  </Button>
                ) : (
                  <Button
                    onClick={applyCoupon}
                    disabled={applyingCoupon || paying}
                    className="h-12 rounded-xl bg-gradient-to-r from-[#5e17eb] to-[#cc00ff] hover:opacity-90"
                  >
                    {applyingCoupon ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Applying...
                      </>
                    ) : (
                      <>
                        <Percent className="h-4 w-4 mr-2" />
                        Apply Coupon
                      </>
                    )}
                  </Button>
                )}
              </div>

              {couponApplied && (
                <div className="mt-4 flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-700">
                  <CheckCircle2 className="h-4 w-4" />
                  Coupon <strong>{pricing.coupon_code}</strong> applied.
                  You saved {formatINR(pricing.coupon_discount || 0)}.
                </div>
              )}
            </div>

            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-lg font-bold text-slate-900 mb-5">
                Billing Information
              </h2>

              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs text-slate-400 mb-1">
                    Business / Legal Name
                  </div>
                  <div className="font-medium">
                    {billing.legal_name ||
                      billing.brand_name ||
                      "MakeMyEventPro"}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-slate-400 mb-1">
                    GSTIN
                  </div>
                  <div className="font-medium">
                    {billing.gstin || "Not configured"}
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <div className="text-xs text-slate-400 mb-1">
                    Registered Address
                  </div>
                  <div className="font-medium">
                    {billing.registered_address || "Not configured"}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-slate-400 mb-1">
                    Billing Email
                  </div>
                  <div className="font-medium">
                    {billing.billing_email || "-"}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-slate-400 mb-1">
                    Billing Phone
                  </div>
                  <div className="font-medium">
                    {billing.billing_phone || "-"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="lg:sticky lg:top-6 bg-white rounded-3xl border border-slate-200 shadow-lg overflow-hidden">
              <div className="p-6 bg-gradient-to-br from-[#5e17eb] to-[#cc00ff] text-white">
                <div className="flex items-center gap-2 mb-2">
                  <ReceiptText className="h-5 w-5" />
                  <span className="text-sm font-medium text-white/80">
                    ORDER SUMMARY
                  </span>
                </div>

                <h2 className="text-2xl font-bold">
                  {plan.name}
                </h2>

                <p className="text-sm text-white/75 mt-1">
                  {plan.validity_days || 30} days subscription
                </p>
              </div>

              <div className="p-6">
                <div className="space-y-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">
                      Plan Price
                    </span>
                    <span className="font-medium">
                      {formatINR(pricing.plan_price || 0)}
                    </span>
                  </div>

                  <div className="flex justify-between text-emerald-600">
          <span>
            Coupon Discount
          </span>
          <span className="font-semibold">
            -{formatINR(pricing.coupon_discount || 0)}
          </span>
        </div>

                  <div className="flex justify-between">
                    <span className="text-slate-500">
                      Taxable Amount
                    </span>
                    <span className="font-medium">
                      {formatINR(pricing.taxable_amount || 0)}
                    </span>
                  </div>

                  {pricing.gst_enabled && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">
                        GST ({pricing.gst_rate || 0}%)
                      </span>
                      <span className="font-medium">
                        {formatINR(pricing.gst_amount || 0)}
                      </span>
                    </div>
                  )}

                  <div className="border-t border-dashed border-slate-200 pt-4 mt-4">
                    <div className="flex justify-between items-end">
                      <div>
                        <div className="text-sm text-slate-500">
                          Total Payable
                        </div>
                        <div className="text-xs text-slate-400 mt-1">
                          Inclusive of applicable taxes
                        </div>
                      </div>

                      <div className="text-2xl font-bold text-slate-900">
                        {formatINR(pricing.total_amount || 0)}
                      </div>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={payNow}
                  disabled={
                    paying ||
                    !pricing.total_amount ||
                    pricing.total_amount <= 0
                  }
                  className="w-full h-14 mt-6 rounded-2xl text-base font-bold bg-gradient-to-r from-[#5e17eb] to-[#cc00ff] hover:opacity-90 shadow-lg shadow-purple-200"
                >
                  {paying ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      Opening Secure Payment...
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-5 w-5 mr-2" />
                      Pay {formatINR(pricing.total_amount || 0)}
                    </>
                  )}
                </Button>

                <div className="flex items-center justify-center gap-2 text-xs text-slate-400 mt-4">
                  <LockKeyhole className="h-3.5 w-3.5" />
                  Secure payment powered by Razorpay Live
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {success && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-white rounded-[32px] shadow-2xl p-8 text-center animate-in zoom-in-95 duration-300">
            <div className="relative mx-auto w-20 h-20 mb-6">
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#5e17eb] to-[#cc00ff] animate-ping opacity-20" />
              <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-[#5e17eb] to-[#cc00ff] flex items-center justify-center shadow-xl">
                <CheckCircle2 className="h-10 w-10 text-white" />
              </div>
            </div>

            <div className="flex items-center justify-center gap-2 text-purple-600 mb-2">
              <Sparkles className="h-4 w-4" />
              <span className="font-semibold text-sm">
                PAYMENT SUCCESSFUL
              </span>
              <Sparkles className="h-4 w-4" />
            </div>

            <h2 className="text-3xl font-extrabold text-slate-900">
              Congratulations!
            </h2>

            <p className="mt-3 text-slate-600">
              {user?.name || "Vendor"}, your subscription is now active.
            </p>

            <div className="mt-5 rounded-2xl bg-purple-50 border border-purple-100 p-5">
              <div className="text-xs text-purple-500 font-semibold uppercase">
                Activated Plan
              </div>
              <div className="text-xl font-bold text-slate-900 mt-1">
                {plan.name}
              </div>
              <div className="text-sm text-slate-500 mt-1">
                Valid for {plan.validity_days || 30} days
              </div>
            </div>

            <p className="text-sm text-slate-500 mt-5">
              Thank you for choosing MakeMyEventPro.com
            </p>

            <div className="grid grid-cols-2 gap-3 mt-7">
              <Button
                variant="outline"
                onClick={downloadInvoice}
                className="h-12 rounded-xl"
              >
                <ReceiptText className="h-4 w-4 mr-2" />
                Invoice
              </Button>

              <Button
                onClick={() => navigate("/vendor/dashboard")}
                className="h-12 rounded-xl bg-gradient-to-r from-[#5e17eb] to-[#cc00ff]"
              >
                Dashboard
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
